import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X } from 'lucide-react';
import { categoriesAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export const Header = () => {
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Handle scroll for sticky header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  };

  // Navigation items - Home + Categories
  const navItems = [
    { name: 'Home', path: '/', slug: 'home' },
    ...categories.slice(0, 5).map(cat => ({
      name: cat.name,
      path: `/category/${cat.slug}`,
      slug: cat.slug
    }))
  ];

  return (
    <header 
      className={`sticky top-0 z-50 bg-white/98 backdrop-blur-md border-b border-gray-100 transition-shadow duration-300 ${
        isScrolled ? 'shadow-md' : 'shadow-sm'
      }`} 
      data-testid="header"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 lg:h-20">
          {/* Logo - Left */}
          <Link to="/" className="flex-shrink-0 flex items-center" data-testid="logo-link">
            <span 
              className="text-2xl md:text-[1.75rem] lg:text-3xl font-extrabold"
              style={{
                fontFamily: "'Nunito', 'Varela Round', 'Poppins', 'Helvetica Neue', Arial, sans-serif",
                background: 'linear-gradient(90deg, #3B82F6 0%, #7C3AED 60%, #9333EA 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '0em',
                fontWeight: 800,
              }}
            >
              iShopGenie
            </span>
          </Link>

          {/* Navigation - Center */}
          <nav className="hidden lg:flex items-center justify-center flex-1 mx-8">
            <div className="flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.slug}
                  to={item.path}
                  className="nav-link px-4 py-2 font-inter font-medium text-sm text-gray-700 hover:text-brand-red relative group"
                  data-testid={`nav-${item.slug}`}
                >
                  {item.name}
                  {/* Hover underline animation */}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-brand-red group-hover:w-3/4 transition-all duration-300 ease-out"></span>
                </Link>
              ))}
              {user && (
                <Link 
                  to="/admin" 
                  className="nav-link px-4 py-2 font-inter font-medium text-sm text-gray-700 hover:text-brand-red relative group"
                  data-testid="nav-admin"
                >
                  Admin
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-brand-red group-hover:w-3/4 transition-all duration-300 ease-out"></span>
                </Link>
              )}
            </div>
          </nav>

          {/* Search + Actions - Right */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <form onSubmit={handleSearch} className="relative" data-testid="search-form">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search deals..."
                className="w-56 xl:w-64 px-4 py-2 pl-10 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red bg-gray-50 text-sm smooth-transition"
                data-testid="search-input"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </form>
            {user && (
              <button
                onClick={logout}
                className="font-inter font-medium text-sm text-gray-600 hover:text-brand-red smooth-transition"
                data-testid="logout-button"
              >
                Logout
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 ml-auto rounded-lg hover:bg-gray-100 smooth-transition"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="relative mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search deals..."
                className="w-full px-4 py-3 pl-10 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-red/30 bg-gray-50 text-sm"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </form>
            
            {/* Mobile Nav Links */}
            {navItems.map((item) => (
              <Link
                key={item.slug}
                to={item.path}
                className="block px-4 py-3 font-inter font-medium text-gray-700 hover:text-brand-red hover:bg-gray-50 rounded-lg smooth-transition"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            
            {user && (
              <>
                <Link 
                  to="/admin" 
                  className="block px-4 py-3 font-inter font-medium text-gray-700 hover:text-brand-red hover:bg-gray-50 rounded-lg smooth-transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Admin
                </Link>
                <button 
                  onClick={() => { logout(); setMobileMenuOpen(false); }} 
                  className="block w-full text-left px-4 py-3 font-inter font-medium text-gray-700 hover:text-brand-red hover:bg-gray-50 rounded-lg smooth-transition"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
